<template>
  <div class="compact-time-line">
    <!-- 浮动按钮触发选择 -->
    <el-popover
        placement="bottom-start"
        trigger="click"
        width="300"
        popper-class="country-popover"
    >
      <template #reference>
        <el-button class="country-trigger" size="small" icon="el-icon-flag">
          选择国家
        </el-button>
      </template>

      <div class="country-selector">
        <el-select
            v-model="selectedCountries"
            multiple
            filterable
            placeholder="搜索选择国家"
            @change="handleCountryChange"
        >
          <el-option
              v-for="country in filteredCountries"
              :key="country.code"
              :label="country.name"
              :value="country.code"
          >
            <img
                :src="`https://flagcdn.com/16x12/${country.code.toLowerCase()}.png`"
                class="flag-option"
            >
            <span>{{ country.name }}</span>
          </el-option>
        </el-select>
      </div>
    </el-popover>

    <!-- 时间线主体 -->
    <div class="time-line-area">
      <div class="time-line">
        <!-- 时间刻度 -->
        <div
            v-for="hour in 24"
            :key="hour"
            class="time-marker"
            :style="{ left: `${(hour/24)*100}%` }"
        >
          <span>{{ hour % 24 }}</span>
        </div>

        <!-- 时间进度条 -->
        <div class="time-progress">
          <div
              v-for="(segment, index) in timeSegments"
              :key="index"
              class="time-segment"
              :style="{
              backgroundColor: segment.color,
              width: `${100/(timeSegments.length)}%`
            }"
          ></div>
        </div>

        <!-- 国旗游标 -->
        <div
            v-for="(group, index) in groupedMarkers"
            :key="index"
            class="flag-cursor"
            :style="{ left: `${group.position}%` }"
        >
          <div class="cursor-line"></div>
          <div class="flag-stack">
            <img
                v-for="(country, i) in group.countries"
                :key="country.code"
                :src="`https://flagcdn.com/32x24/${country.code.toLowerCase()}.png`"
                :alt="country.name"
                :title="`${country.name} ${group.timeStr}`"
                :style="{ right: `${i*16}px`, zIndex: group.countries.length - i }"
                class="stacked-flag"
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment-timezone';
import axios from 'axios';
import Qs from "qs";

export default {
  name: 'FloatingTimeLine',
  props: {
    toleranceMinutes: {
      type: Number,
      default: 15
    }
  },
  data() {
    return {
      allCountries: [],
      filteredCountries: [],
      selectedCountries: [],
      countriesData: [],
      timeSegments: [],
      updateInterval: null,
      groupedMarkers: [],
      searchQuery: ''
    };
  },
  computed: {
    selectLabel() {
      return country => `${country.zhName || country.name} (${country.timezone.replace(/_/g, ' ')})`;
    }
  },
  created() {
    this.fetchCountries();
  },
  mounted() {
    this.updateInterval = setInterval(this.updateCountries, 60000);
    this.initTimeLine();
  },
  beforeDestroy() {
    clearInterval(this.updateInterval);
  },
  methods: {
    // 同前代码...
    fetchCountries() {
      axios({
        url: '/apiserve/crmapi/system.asp',
        method: 'post',
        transformRequest: [function(data) { return Qs.stringify(data) }],
        data: {
          action: 'countries',
        }
      }).then(response => {
        this.allCountries = response.data.countries.map(country => ({
          ...country,
          selectLabel: this.selectLabel(country)
        }));
        this.filteredCountries = [...this.allCountries];
        this.selectedCountries = this.allCountries
            .filter(c => c.show)
            .map(c => c.code);
        this.updateCountries();
      }).catch(error => {
        console.error('获取国家数据失败:', error);
      });
    },

    updateCountryShowStatus() {
      axios({
        url: '/apiserve/crmapi/system.asp',
        method: 'post',
        transformRequest: [function(data) { return Qs.stringify(data) }],
        data: {
          action: 'countriesupdate',
          selectcountries:this.selectedCountries.join()
        }
      }).catch(error => {
        console.error('更新国家状态失败:', error);
      });
    },

    handleCountryChange() {
      this.updateCountries();
      this.updateCountryShowStatus();
    },

    initTimeLine() {
      const segments = [];
      for (let hour = 0; hour < 24; hour++) {
        let color;
        if ((hour >= 8 && hour < 9) || (hour >= 17 && hour < 21)) {
          color = '#90EE90'; // 浅绿色
        } else if (hour >= 9 && hour < 17) {
          color = '#4CAF50'; // 深绿色
        } else {
          color = '#E0E0E0'; // 灰色
        }
        segments.push({ hour, color });
      }
      this.timeSegments = segments;
    },

    updateCountries() {
      if (!this.allCountries.length) return;

      this.countriesData = this.selectedCountries.map(code => {
        const country = this.allCountries.find(c => c.code === code);
        if (!country) return null;

        const now = moment().tz(country.timezone);
        const hours = now.hours();
        const minutes = now.minutes();

        return {
          ...country,
          hours,
          minutes,
          timeStr: now.format('HH:mm'),
          totalMinutes: hours * 60 + minutes,
          position: (hours * 60 + minutes) / (24 * 60) * 100
        };
      }).filter(Boolean);

      this.groupTimeMarkers();
    },

    groupTimeMarkers() {
      if (!this.countriesData.length) {
        this.groupedMarkers = [];
        return;
      }

      const sorted = [...this.countriesData].sort((a, b) => a.totalMinutes - b.totalMinutes);
      const groups = [];
      let currentGroup = {
        countries: [],
        min: sorted[0].totalMinutes - this.toleranceMinutes - 1,
        max: sorted[0].totalMinutes + this.toleranceMinutes
      };

      sorted.forEach(country => {
        if (country.totalMinutes <= currentGroup.max) {
          currentGroup.countries.push(country);
          currentGroup.max = Math.max(currentGroup.max, country.totalMinutes + this.toleranceMinutes);
        } else {
          this.finalizeGroup(currentGroup, groups);
          currentGroup = {
            countries: [country],
            min: country.totalMinutes - this.toleranceMinutes,
            max: country.totalMinutes + this.toleranceMinutes
          };
        }
      });

      this.finalizeGroup(currentGroup, groups);
      this.groupedMarkers = groups;
    },

    finalizeGroup(group, groups) {
      if (group.countries.length === 0) return;

      const total = group.countries.reduce((sum, c) => sum + c.totalMinutes, 0);
      const averageMinutes = total / group.countries.length;
      const position = (averageMinutes / (24 * 60)) * 100;

      groups.push({
        countries: group.countries,
        position,
        timeStr: group.countries[0].timeStr
      });
    }
  }
};
</script>

<style scoped>
/* 添加时间线段颜色过渡效果 */
.time-segment {
  transition: background-color 0.5s ease;
}
/* 保持其他样式不变 */

.compact-time-line {
  display: flex;
  align-items: center;
  height: 80px;
  width: 100%;
  padding: 5px;
  box-sizing: border-box;
}

.country-trigger {
  margin-right: 10px;
}

.time-line-area {
  flex: 1;
  position: relative;
  height: 100%;
}

.time-line {
  position: relative;
  height: 60px;
  width: 100%;
}

.time-progress {
  position: absolute;
  top: 30px;
  left: 0;
  right: 0;
  height: 4px; /* 更细的线 */
  display: flex;
  border-radius: 2px;
  overflow: hidden;
  background-color: #f5f5f5;
}

.time-segment {
  height: 100%;
  transition: background-color 0.3s ease;
}

.time-marker {
  position: absolute;
  top: 10px;
  transform: translateX(-50%);
  font-size: 12px;
  color: #666;
}

.flag-cursor {
  position: absolute;
  top: 15px; /* 紧贴时间线 */
  transform: translateX(-50%);
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.cursor-line {
  width: 1px;
  height: 20px; /* 延长连接线 */
  background-color: #3a84ff;
}

.flag-stack {
  position: relative;
  height: 32px;
  min-width: 32px;
  margin-top: -10px; /* 向上偏移 */
}

.stacked-flag {
  position: absolute;
  bottom: 0;
  width: 25px; /* 放大国旗 */
  border: 1px solid #eee;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  transition: all 0.2s ease;
}

.stacked-flag:hover {
  transform: scale(1.3);
  z-index: 3;
}

/* 浮动窗口样式 */
.country-popover {
  margin-top: 5px;
}

.country-selector {
  padding: 10px;
}

.country-selector >>> .el-select {
  width: 100%;
}

.flag-option {
  width: 16px;
  height: 12px;
  margin-right: 8px;
  vertical-align: middle;
}
</style>
